import { Injectable } from '@angular/core'
import { Observable, of, throwError } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { GenericCoordinates } from '../../location.types'
import {
  LocationSearchService,
  LocationSearchResult,
  AwsLocationSearchResponse,
  AwsLocationSearchResult
} from '../location.interface'
import { DrrHttpClient } from '../../../../http/drr-http-client'
import { DEFAULT_LAT, DEFAULT_LNG } from '../../location.constants'

@Injectable({ providedIn: 'root' })
export class AwsLocationSearchService implements LocationSearchService {

  constructor(private http: DrrHttpClient) {}

  search(term: string, location?: GenericCoordinates): Observable<LocationSearchResult[]> {
    const requestBody: any = {
      text: term
    }

    if (location && location.latitude && location.longitude) {
      requestBody.latitude = parseFloat(location.latitude.toString())
      requestBody.longitude = parseFloat(location.longitude.toString())
    } else {
      requestBody.latitude = DEFAULT_LAT
      requestBody.longitude = DEFAULT_LNG
    }

    return this.http
      .post<AwsLocationSearchResponse>('bff/roadservice/geo/suggest', requestBody)
      .pipe(
        map((response) => this.transformAwsResponseToLocationSearchResults(response)),
        // An empty suggest payload is a business outcome, not a transport error;
        // callers key off the 'ZERO_RESULTS' string to show the "no matches" state.
        switchMap((results) => (results.length ? of(results) : throwError('ZERO_RESULTS')))
      )
  }

  private transformAwsResponseToLocationSearchResults(response: AwsLocationSearchResponse): LocationSearchResult[] {
    if (!response.ResultItems || response.ResultItems.length === 0) {
      return []
    }

    let allResults = response.ResultItems.filter(result => result.Place && result.Place.PlaceId)

    // Filter duplicates based on SuggestResultItemType and Title combination
    allResults = this.filterDuplicatesByTypeAndTitle(allResults)

    const landmarks = allResults.filter(result => result.Place.PlaceType === 'PointOfInterest')

    if (landmarks.length > 0) {
      const landmarkLabels = landmarks.map(l => l.Place.Address.Label)
      allResults = allResults.filter(result => {
        if (result.Place.PlaceType === 'PointOfInterest') {
          return true // always keep landmarks
        }
        // For non-landmarks, check if it's a duplicate of a landmark
        const nonLandmarkLabel = result.Place.Address.Label
        const isDuplicate = landmarkLabels.some(landmarkLabel => landmarkLabel.includes(nonLandmarkLabel))
        return !isDuplicate
      })
    }

    return allResults.map(result => this.transformAwsResultToLocationSearchResult(result))
  }

  private filterDuplicatesByTypeAndTitle(results: AwsLocationSearchResult[]): AwsLocationSearchResult[] {
    const seen = new Set<string>()
    const uniqueResults: AwsLocationSearchResult[] = []

    for (const result of results) {
      const key = `${result.SuggestResultItemType}|${result.Title}`
      if (!seen.has(key)) {
        seen.add(key)
        uniqueResults.push(result)
      }
    }

    return uniqueResults
  }

  private transformAwsResultToLocationSearchResult(awsResult: AwsLocationSearchResult): LocationSearchResult {
    const title = awsResult.Title
    const addressLabel = awsResult.Place.Address.Label
    const placeType = awsResult.Place.PlaceType

    // Determine if this is a landmark based on PlaceType
    const isLandmark = placeType === 'PointOfInterest'

    // Parse the address to extract main_text and secondary_text
    const addressParts = addressLabel.split(', ')
    const secondaryText = addressParts.slice(1).join(', ') || ''

    return {
      description: addressLabel,
      main_text: addressParts[0],
      secondary_text: secondaryText,
      landmark: isLandmark ? title : null,
      place_id: awsResult.Place.PlaceId
    }
  }
}
